import { CompactText } from 'ui/CompactText';
import type React from 'react';
import { useEffect, useState } from 'react';

import { createChatReport, getChatReportReasons } from 'api/v1/requests/chatReports';
import { addNotification } from 'notifications';
import { Modal } from 'ui/Modal';
import { TextField } from 'ui/TextField';

import classes from './ChatReportModal.module.scss';

type ChatReportModalProps = {
  open: boolean;
  onClose: () => void;
  chatId: number;
  peerLabel?: string | null;
};

export const ChatReportModal: React.FC<ChatReportModalProps> = ({
  open,
  onClose,
  chatId,
  peerLabel,
}) => {
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Причина не выбрана заранее: пользователь выбирает ее сам, ошибка показывается под группой.
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [textError, setTextError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingReasons(true);
    getChatReportReasons()
      .then((data) => {
        if (cancelled) return;
        setReasons(data);
      })
      .catch(() => {
        if (!cancelled) {
          addNotification('Не удалось загрузить причины жалобы', 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingReasons(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setReasonCode('');
      setReasonText('');
      setReasonError(null);
      setTextError(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!reasonCode) {
      setReasonError('Выберите причину жалобы');
      return;
    }
    if (reasonCode === 'other' && !reasonText.trim()) {
      setTextError('Для причины «Другое» опишите ситуацию');
      return;
    }
    setSubmitting(true);
    try {
      await createChatReport({
        chat_id: chatId,
        reason_code: reasonCode,
        reason_text: reasonText.trim() || null,
      });
      addNotification('Жалоба отправлена модераторам', 'success');
      onClose();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Не удалось отправить жалобу';
      addNotification(String(detail), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} widthModal='540px' zIndex={1300}>
      <div className={classes.root}>
        <h2 className={classes.title}>Жалоба на чат</h2>
        <p className={classes.subtitle}>
          {peerLabel
            ? `Чат с ${peerLabel}. Опишите, что произошло, чтобы команда могла рассмотреть жалобу.`
            : 'Опишите, что произошло, чтобы команда могла рассмотреть жалобу.'}
        </p>

        {loadingReasons ? (
          <CompactText className={classes.hint} tone={'info'}>Загрузка причин...</CompactText>
        ) : (
          <div className={classes.reasons} role='radiogroup' aria-label='Выберите причину жалобы'>
            <p className={classes.reasonLabel}>Выберите причину жалобы</p>
            {Object.entries(reasons).map(([code, label]) => (
              <label key={code} className={classes.reason}>
                <input
                  type='radio'
                  name='chat-report-reason'
                  value={code}
                  checked={reasonCode === code}
                  onChange={() => {
                    setReasonCode(code);
                    setReasonError(null);
                    if (code !== 'other') setTextError(null);
                  }}
                />
                <span>{label}</span>
              </label>
            ))}
            {reasonError ? (
              <p className='fig-field-error' role='alert'>
                {reasonError}
              </p>
            ) : null}
          </div>
        )}

        <TextField
          value={reasonText}
          onChange={(value: string) => {
            setReasonText(value);
            if (textError && value.trim()) setTextError(null);
          }}
          placeholder='Комментарий (необязательно)'
          tag='textarea'
          rows={3}
          widthFullSize
          inputContainerClassName={classes.comment}
        />
        {textError ? (
          <p className='fig-field-error' role='alert'>
            {textError}
          </p>
        ) : null}

        <div className={classes.actions}>
          <button type='button' className={classes.cancel} onClick={onClose} disabled={submitting}>
            Отмена
          </button>
          <button
            type='button'
            className={classes.submit}
            onClick={handleSubmit}
            disabled={submitting || loadingReasons}
          >
            {submitting ? 'Отправка...' : 'Отправить жалобу'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
